// src/components/settings/settingsTabs.tsx
import React from "react";

import { Company } from "../../types/admin/CompanyTypes";
import { User } from "../../types/admin/UserTypes";
import { hasFeature } from "../../utils/subscriptions/subscriptionService";
import { can, canAdministerClinic, canViewOpsDashboards, hasInventoryAccess, staffCan } from "../../utils/userPrefs/validatePermissions";
import { ENV } from "../../config/runtimeEnv";

import UserPreferencesTab from "../../pages/settings/UserPreferencesTab";
import BrandingConfiguration from "../../pages/settings/settings/BrandingConfiguration";
import CompanyHours from "../../pages/settings/settings/CompanyHours";
import CustomTestPackages from "../../pages/settings/settings/CustomTestPackages";
import CompanyEmailConfiguration from "../../pages/settings/settings/CompanyEmailConfiguration";
import UserEmailConfiguration from "../../pages/settings/settings/UserEmailConfiguration";
import GlobalResourceLinksManagement from "../../pages/settings/settings/GlobalResourceLinksManagement";
import GlobalFileResourcesManagement from "../../pages/settings/settings/GlobalFileResourcesManagement";
import ClinicDocumentStorage from "../../pages/settings/settings/ClinicDocumentStorage";
import IntegrationManagement from "../../pages/settings/settings/IntegrationManagement";
import SmsConfiguration from "../../pages/settings/settings/SmsConfiguration";
import TeamTab from "../../pages/settings/team/TeamTab";
import AppointmentTypesTab from "../../pages/settings/appointments/AppointmentTypesTab";
import MedicationCatalogTab from "../../pages/settings/medicationCatalog/MedicationCatalogTab";
import TemplatesTab from "../../pages/settings/TemplatesTab";
import XoePermissionsTab from "../../pages/settings/xoe/XoePermissionsTab";
import XoeMemoryTab from "../../pages/settings/xoe/XoeMemoryTab";
import InvoicingSettingsTab from "../../pages/finance/settings/InvoicingSettingsTab";
import CategoryManagement from "../../pages/settings/inventory/CategoryManagement";
import SupplierManagement from "../../pages/settings/inventory/SupplierManagement";
import ShopifySettings from "../../pages/settings/inventory/ShopifySettings";
import JobQueuesTab from "../../pages/settings/system/JobQueuesTab";
import { InlineLoading } from "../shared";

// Lazy (the ScreeningQuestionsTab precedent) so the Cache tab and its charts stay out of the entry chunk until opened.
const CacheTab = React.lazy(() => import("../../pages/settings/system/CacheTab"));

/**
 * ettings drawer's tab registry.

 */
export interface SettingsTabContext {
	company: Company | null;
	onCompanyUpdated: (c: Company) => void;
	user: User;
}

export interface SettingsTabDef {
	key: string;
	label: string;
	/** The tab's own permission, as its page's routes declare it, plus any subscription feature. */
	visible: () => boolean;
	render: (ctx: SettingsTabContext) => React.ReactNode;
}

export const isSettingsRailHeading = (key: string): boolean => key.startsWith("__heading_");
const isPeptideOnly = (): boolean => hasFeature("healthcare.peptide") && !hasFeature("healthcare");

/**
 * The ordered rail. Tab ORDER is the order `CompanySettingsPage` rendered them in.
 */
export const SETTINGS_TABS: SettingsTabDef[] = [
	{
		key: "__heading_account__",
		label: "My account",
		visible: () => true,
		render: () => null,
	},
	{
		key: "preferences",
		label: "User Preferences",
		visible: () => true,
		render: () => <UserPreferencesTab />,
	},
	{
		key: "my-email",
		label: "My Email",
		visible: () => hasFeature("settings") && staffCan("notification.email_settings.manage"),
		render: (ctx) => <UserEmailConfiguration userId={ctx.user.uuid} />,
	},
	{
		key: "__heading_clinic__",
		label: "Clinic",
		visible: () => true,
		render: () => null,
	},
	{
		key: "branding",
		label: "Branding",
		visible: () => hasFeature("whitelabel") && staffCan("healthcare.branding.manage"),
		render: () => <BrandingConfiguration />,
	},
	{
		key: "company-hours",
		label: "Company Hours",
		visible: () => hasFeature("settings") && staffCan("healthcare.companies.manage"),
		render: (ctx) => <CompanyHours company={ctx.company!} onCompanyUpdated={ctx.onCompanyUpdated} />,
	},
	{
		key: "email",
		label: "Email Configuration",
		visible: () => hasFeature("settings") && staffCan("notification.clinic_email.manage"),
		render: (ctx) => <CompanyEmailConfiguration companyId={ctx.company!.id} />,
	},
	{
		key: "global-resources",
		label: "Global Resources",
		visible: () => hasFeature("settings") && staffCan("file.resources.write"),
		render: (ctx) => <GlobalResourceLinksManagement companyId={ctx.company!.id} />,
	},
	{
		key: "file-resources",
		label: "File Resources",
		visible: () => hasFeature("settings") && staffCan("file.clinic_documents.read"),
		render: () => <GlobalFileResourcesManagement />,
	},
	{
		key: "document-storage",
		label: "Document Storage",
		visible: () => hasFeature("settings") && staffCan("file.resources.write"),
		render: () => <ClinicDocumentStorage />,
	},
	{
		key: "integrations",
		label: "Integrations",
		visible: () => hasFeature("settings") && staffCan("healthcare.integrations.manage") && !isPeptideOnly(),
		render: (ctx) => <IntegrationManagement companyId={ctx.company!.id} />,
	},
	{
		key: "sms",
		label: "SMS",
		visible: () => hasFeature("settings") && staffCan("notification.sms.manage"),
		render: () => <SmsConfiguration />,
	},
	{
		key: "team",
		label: "Team Management",
		// Reading user administration keeps the team view for those who may see staff but not change them (Chief Medical Officer).
		visible: () => hasFeature("settings") && (canAdministerClinic() || staffCan("healthcare.users.read")),
		render: () => <TeamTab />,
	},
	{
		key: "__heading_clinical__",
		label: "Clinical",
		visible: () => true,
		render: () => null,
	},
	{
		key: "appointment-types",
		label: "Appointment Types",
		visible: () => hasFeature("settings") && !isPeptideOnly() && staffCan("healthcare.appointment_types.manage"),
		render: () => <AppointmentTypesTab />,
	},
	{
		key: "test-packages",
		label: "Custom Test Packages",
		visible: () => hasFeature("settings") && staffCan("healthcare.catalogue.manage") && !isPeptideOnly(),
		render: () => <CustomTestPackages />,
	},
	{
		key: "medication-catalog",
		label: "Medication Catalog",
		visible: () => hasFeature("settings") && staffCan("healthcare.catalogue.manage"),
		render: () => <MedicationCatalogTab />,
	},
	{
		key: "templates",
		label: "Templates",
		visible: () => hasFeature("settings") && staffCan("healthcare.templates.manage"),
		render: () => <TemplatesTab />,
	},
	{
		key: "xoe",
		label: "Xoe Permissions",
		visible: () =>
			hasFeature("settings") &&
			ENV.VITE_XOE_ENABLED === "true" &&
			!!ENV.VITE_XOE_AGENT_SERVER &&
			staffCan("xoe_agent.settings.manage"),
		render: () => <XoePermissionsTab />,
	},
	{
		key: "xoe-memory",
		label: "Xoe Memory",
		visible: () =>
			hasFeature("settings") &&
			ENV.VITE_XOE_ENABLED === "true" &&
			!!ENV.VITE_XOE_AGENT_SERVER &&
			staffCan("xoe_agent.memories.read"),
		render: () => <XoeMemoryTab />,
	},
	{
		key: "__heading_commerce__",
		label: "Commerce",
		visible: () => true,
		render: () => null,
	},
	{
		key: "invoicing",
		label: "Invoicing & Tax",
		visible: () => staffCan("finance.settings.manage"),
		render: () => <InvoicingSettingsTab />,
	},
	{
		key: "categories",
		label: "Product Categories",
		visible: () => hasInventoryAccess(),
		render: (ctx) => <CategoryManagement user={ctx.user} companyId={ctx.company!.id} />,
	},
	{
		key: "suppliers",
		label: "Suppliers",
		visible: () => hasInventoryAccess(),
		render: () => <SupplierManagement />,
	},
	{
		key: "shopify",
		label: "Shopify",
		visible: () => hasInventoryAccess() && can("commerce.shopify.manage"),
		render: () => <ShopifySettings />,
	},
	{
		key: "__heading_system__",
		label: "System",
		visible: () => true,
		render: () => null,
	},
	{
		key: "job-queues",
		label: "Job Queues",
		visible: () => canViewOpsDashboards(),
		render: () => <JobQueuesTab />,
	},
	{
		key: "cache",
		label: "Cache",
		// Same gate as job-queues, for the same reason — decision D1 (ClickUp 86d3q991k).
		visible: () => canViewOpsDashboards(),
		render: () => (
			<React.Suspense fallback={<InlineLoading minHeight={200} message="Loading cache status…" />}>
				<CacheTab />
			</React.Suspense>
		),
	},
];


export const resolveSettingsRail = (tabs: SettingsTabDef[]): SettingsTabDef[] => {
	const out: SettingsTabDef[] = [];

	for (let i = 0; i < tabs.length; i += 1) {
		const entry = tabs[i];

		if (!isSettingsRailHeading(entry.key)) {
			if (entry.visible()) out.push(entry);
			continue;
		}

		if (!entry.visible()) continue;

		let headsSomething = false;
		for (let j = i + 1; j < tabs.length && !isSettingsRailHeading(tabs[j].key); j += 1) {
			if (tabs[j].visible()) {
				headsSomething = true;
				break;
			}
		}
		if (headsSomething) out.push(entry);
	}

	return out;
};

/** The rail as it should be rendered right now, for the real registry. */
export const visibleSettingsTabs = (): SettingsTabDef[] => resolveSettingsRail(SETTINGS_TABS);

/**
 * The visible entries a user can actually land on — headings excluded. This is the list
 * "is there anything in Settings at all?" is answered from, and the list a requested tab
 * key is validated against.
 */
export const selectableSettingsTabs = (): SettingsTabDef[] =>
	visibleSettingsTabs().filter((t) => !isSettingsRailHeading(t.key));
